import { createReducer } from 'typesafe-actions';
import {
  addFileUpload,
  completeFileUpload,
  failFileUpload,
  fileUploadRemoved,
  setFileUploadPreview,
  setFileUploadTarget,
  startFileUpload,
  updateFileUploadProgress,
} from './actions';
import type { FileUpload, FileUploadState } from './types';

const initialState: FileUploadState = {
  uploads: [],
};

const updateUpload = (
  state: FileUploadState,
  uploadId: string,
  update: (upload: FileUpload) => FileUpload,
): FileUploadState => {
  const index = state.uploads.findIndex(
    (upload) => upload.uploadId === uploadId,
  );
  if (index < 0) {
    return state;
  }

  return {
    ...state,
    uploads: state.uploads.map((upload, currentIndex) =>
      currentIndex === index ? update(upload) : upload,
    ),
  };
};

export const fileUploadReducer = createReducer(initialState)
  .handleAction(addFileUpload, (state, action) => {
    const { contentType, file, filename, totalBytes, uploadId } =
      action.payload;
    if (state.uploads.some((upload) => upload.uploadId === uploadId)) {
      return state;
    }

    return {
      ...state,
      uploads: [
        ...state.uploads,
        {
          uploadId,
          file,
          filename,
          contentType,
          totalBytes,
          uploadedBytes: 0,
          status: 'preparing',
          previewUri: null,
        },
      ],
    };
  })
  .handleAction(setFileUploadTarget, (state, action) =>
    updateUpload(state, action.payload.uploadId, (upload) =>
      upload.status === 'preparing'
        ? {
            ...upload,
            target: action.payload.target,
            remoteId: action.payload.target.remoteId,
            status: 'queued',
          }
        : upload,
    ),
  )
  .handleAction(startFileUpload, (state, action) =>
    updateUpload(state, action.payload.uploadId, (upload) =>
      upload.status === 'queued'
        ? { ...upload, status: 'uploading', uploadedBytes: 0 }
        : upload,
    ),
  )
  .handleAction(updateFileUploadProgress, (state, action) =>
    updateUpload(state, action.payload.uploadId, (upload) =>
      upload.status === 'uploading'
        ? {
            ...upload,
            uploadedBytes: action.payload.uploadedBytes,
            totalBytes: action.payload.totalBytes,
          }
        : upload,
    ),
  )
  .handleAction(completeFileUpload, (state, action) =>
    updateUpload(state, action.payload.uploadId, (upload) =>
      upload.status === 'uploading'
        ? {
            ...upload,
            status: 'uploaded',
            uploadedBytes: upload.totalBytes,
          }
        : upload,
    ),
  )
  .handleAction(failFileUpload, (state, action) =>
    updateUpload(state, action.payload.uploadId, (upload) =>
      upload.status === 'preparing' || upload.status === 'uploading'
        ? {
            ...upload,
            status: 'error',
            error: action.payload.error,
          }
        : upload,
    ),
  )
  .handleAction(setFileUploadPreview, (state, action) =>
    updateUpload(state, action.payload.uploadId, (upload) =>
      upload.file === action.payload.file && !upload.previewUri
        ? {
            ...upload,
            previewUri: action.payload.previewUri,
          }
        : upload,
    ),
  )
  .handleAction(fileUploadRemoved, (state, action) => ({
    ...state,
    uploads: state.uploads.filter(
      (upload) => upload.uploadId !== action.payload.uploadId,
    ),
  }));
